// ============================================
// User Profile Firestore Service
// CRUD for user_profiles collection.
// Pre-auth: keyed by anonymous sessionId.
// Post-auth: migrated to Firebase Auth UID.
// ============================================

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { UserProfile, AccountStatus } from '../../types';
import { C_USER_PROFILES } from '../../constants/collections';

const COLLECTION = C_USER_PROFILES;

/** Get a user profile by session/user ID. Returns null if not found. */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, COLLECTION, userId));
  return snap.exists() ? (snap.data() as UserProfile) : null;
}

/** Create or update a user profile. */
export async function saveProfile(userId: string, profile: UserProfile): Promise<void> {
  await setDoc(doc(db, COLLECTION, userId), stripUndefined(profile));
}

/** Save email to a user profile (merge — won't clobber other fields). */
export async function saveEmail(userId: string, email: string): Promise<void> {
  await setDoc(
    doc(db, COLLECTION, userId),
    {
      uid: userId,
      email,
      emailCollectedAt: Timestamp.now(),
    },
    { merge: true },
  );
}

/** Ensure a profile exists for this session. Creates a minimal one if missing. */
export async function ensureProfile(
  userId: string,
  defaults?: Partial<UserProfile>,
): Promise<UserProfile> {
  const existing = await getProfile(userId);
  if (existing) return existing;

  const profile: UserProfile = {
    uid: userId,
    goals: [],
    medications: [],
    conditions: [],
    allergies: [],
    createdAt: new Date().toISOString(),
    accountStatus: 'active',
    acceptedTosVersion: null,
    acceptedPrivacyVersion: null,
    acceptedAt: null,
    suspendedAt: null,
    suspensionReason: null,
    deletionRequestedAt: null,
    deletionScheduledAt: null,
    ...defaults,
  };

  await saveProfile(userId, profile);
  return profile;
}

/** Update account status (suspend, terminate, reactivate). */
export async function updateAccountStatus(
  userId: string,
  status: AccountStatus,
  reason?: string,
): Promise<void> {
  const profile = await getProfile(userId);
  if (!profile) return;

  const updated: UserProfile = {
    ...profile,
    accountStatus: status,
    suspendedAt: status === 'suspended' ? new Date().toISOString() : profile.suspendedAt,
    suspensionReason: reason ?? profile.suspensionReason,
  };

  await saveProfile(userId, updated);
}

/** Record that user accepted current TOS + Privacy versions. */
export async function recordPolicyAcceptance(
  userId: string,
  tosVersion: string,
  privacyVersion: string,
): Promise<void> {
  const profile = await getProfile(userId);
  if (!profile) return;

  await saveProfile(userId, {
    ...profile,
    acceptedTosVersion: tosVersion,
    acceptedPrivacyVersion: privacyVersion,
    acceptedAt: new Date().toISOString(),
  });
}

/** Record deletion request with 24h cooling-off. */
export async function recordDeletionRequest(userId: string): Promise<void> {
  const profile = await getProfile(userId);
  if (!profile) return;

  const now = new Date();
  const scheduled = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  await saveProfile(userId, {
    ...profile,
    deletionRequestedAt: now.toISOString(),
    deletionScheduledAt: scheduled.toISOString(),
  });
}

/** Cancel a pending deletion request (during cooling-off). */
export async function cancelDeletionRequest(userId: string): Promise<void> {
  const profile = await getProfile(userId);
  if (!profile) return;

  await saveProfile(userId, {
    ...profile,
    deletionRequestedAt: null,
    deletionScheduledAt: null,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object') ? stripUndefined(value) : value;
    }
  }
  return cleaned;
}
